/**
 * Tracking Event Constants
 * Centralized event names for Amplitude tracking
 */

export const TRACKING_EVENTS = {
    // Authentication Events
    AUTH: {
        SIGN_IN_FORM_SUBMITTED: 'sign_in_form_submitted',
        SIGN_UP_FORM_SUBMITTED: 'sign_up_form_submitted',
        SIGN_IN_FORM_ERROR: 'sign_in_form_error',
        SIGN_UP_FORM_ERROR: 'sign_up_form_error',
        LOGOUT_CLICKED: 'logout_clicked',
        LOGIN_SUCCESS: 'login_success',
        REGISTRATION_SUCCESS: 'registration_success',
        SSO_CLICKED: 'sso_clicked',
        // Fired after a successful Google SSO redirect back to the site
        // (URL has ?sso_provider=google). Complements login_success /
        // registration_success with a Google-specific event name.
        GOOGLE_AUTH_COMPLETED: 'google_auth_completed',
    },

    // Banner Events
    BANNERS: {
        // Previous: 'providers_banner_click' (for provider banners)
        // Previous: 'get_matched_small' / 'get_matched_big' (for match forms)
        CLICKED: 'banner_clicked',
        PROMO_VIEWED: 'promo_banner_viewed',
        PROMO_CTA_CLICKED: 'promo_banner_cta_clicked',
        PROMO_CLOSED: 'promo_banner_closed',
    },

    // Provider Events
    PROVIDERS: {
        // Previous: 'provider_book_a_consultation' (button click) + 'provider_ecom_cta_clicked'
        CTA_CLICKED: 'provider_cta_clicked',
        // Previous: 'provider_book_a_consultation_sent' (same name, now uses constant)
        BOOK_CONSULTATION_SENT: 'provider_book_a_consultation_sent',
        // Previous: 'provider_product_buy_clicked'
        PRODUCT_CTA_CLICKED: 'provider_product_cta_clicked',
        CARD_CLICKED: 'provider_card_clicked',
        CONTACT_CLICKED: 'provider_contact_clicked',
        FILTERED: 'providers_filtered',
        // Booking flow tracking (via Amelia JS hooks)
        BOOKING_STEP: 'provider_booking_step',
        BOOKING_COMPLETED: 'provider_booking_completed',
        // Note: Provider page views now use ENGAGEMENT.PAGE_VIEW with page_type='provider_page'
        // Previous: 'provider_page_view'
        // Provider page scrolls now use ENGAGEMENT.PAGE_SCROLL with page_type='provider_page'
        // Previous: 'provider_page_scroll'
    },

    // Form Events
    FORMS: {
        NEWSLETTER_SUBSCRIPTION_ATTEMPTED: 'newsletter_subscription_attempted',
        FORM_SUBMITTED: 'form_submitted',
    },

    // Community Events
    COMMUNITY: {
        REPLY_SUBMITTED: 'community_reply_submitted',
        TOPIC_SUBMITTED: 'community_topic_submitted',
        REPLY_EDITED: 'community_reply_edited',
        TOPIC_EDITED: 'community_topic_edited',
        REGISTRATION_FORM_SUBMITTED: 'community_registration_form_submitted',
        SIGN_IN_FORM_SUBMITTED: 'community_sign_in_form_submitted',
        // Fired on statesofmind.com handoff page right before redirecting to Circle.
        // Source code: inc/circle-handoff.php :: pe_circle_sso_render_handoff().
        HANDOFF_STARTED: 'community_handoff_started',
        // Fired by the Amplitude snippet inside Circle on the first page load
        // after the SSO redirect. The snippet runs on Circle (community.statesofmind.com),
        // but its body is server-rendered by WordPress so the Amplitude API key never
        // has to be pasted into Circle by hand:
        //   - Source code: inc/circle-tracking-snippet.php (theme).
        //   - Endpoint:    GET https://statesofmind.com/circle-tracking
        //   - Install:     <script async src="…/circle-tracking"></script> in
        //                  Circle Admin → Settings → Code Snippets → Head.
        // Updates flow through normal WP deploy + FastCGI purge; rotating the
        // Amplitude key is a single env-var change on DigitalOcean.
        USER_ENTERED: 'user_entered_community',
    },

    // Test Events
    TEST: {
        REPORT_VIEWED: 'test_report_viewed',
        ARTICLE_CLICKED: 'article_clicked_from_report',
        SEE_ALL_ARTICLES_CLICKED: 'see_all_articles_clicked',
        INVITE_OTHERS_CLICKED: 'invite_others_clicked',
        TAKE_ANOTHER_TEST_CLICKED: 'take_another_test_clicked',
        FIND_PROVIDER_TEST_BANNER_CLICKED: 'find_a_provider_test_banner_clicked',
        CATEGORY_ARROW_CLICKED: 'category_arrow_clicked',
        SEE_ALL_TESTS_CLICKED: 'see_all_tests_clicked',
        TEST_CLICKED: 'test_clicked_from_report',
        GENDER_SELECTED: 'test_landing_gender_selected',
        START_CLICKED: 'test_landing_start_clicked',
    },

    // Navigation Events
    NAVIGATION: {
        HEADER_CTA_CLICKED: 'header_cta_clicked',
        TABLE_OF_CONTENTS_CLICKED: 'toc_clicked',
    },

    // Engagement Events
    ENGAGEMENT: {
        // Page Tracking
        // Previous: 'provider_page_view' (now uses page_type='provider_page')
        PAGE_VIEW: 'page_viewed',
        // Previous: 'provider_page_scroll' (now uses page_type='provider_page')
        PAGE_SCROLL: 'page_scrolled',
        // Social Media
        DISCORD_BUTTON_CLICKED: 'discord_button_clicked',
        FACEBOOK_BUTTON_CLICKED: 'facebook_button_clicked',
        WHATSAPP_BUTTON_CLICKED: 'whatsapp_button_clicked',
        CONTENT_SHARED: 'content_shared',
        // User Interactions
        REACTION_CLICKED: 'reaction_clicked',
        FEEDBACK_VOTE_SUBMITTED: 'feedback_vote_submitted',
        FEEDBACK_DETAILS_SUBMITTED: 'feedback_details_submitted',
        // UI Interactions
        POPUP_OPENED: 'popup_opened',
        POPUP_CLOSED: 'popup_closed',
        FAQ_ITEM_CLICKED: 'faq_v2_item_clicked',
    },

    // Psychedelic Evidence Tracker (/psychedelic-evidence-tracker/ and child pages)
    EVIDENCE_TRACKER: {
        SEARCHED: 'evidence_tracker_searched',
        FILTERED: 'evidence_tracker_filtered',
        VIEW_SWITCHED: 'evidence_tracker_view_switched',
        CELL_OPENED: 'evidence_tracker_cell_opened',
        LINK_CLICKED: 'evidence_tracker_link_clicked',
        DATA_DOWNLOADED: 'evidence_tracker_data_downloaded',
        CITATION_COPIED: 'evidence_tracker_citation_copied',
        FAQ_OPENED: 'evidence_tracker_faq_opened',
        // Clicks into the tracker from outside it (e.g. Start Here, Step 6)
        ENTRY_CLICKED: 'evidence_tracker_entry_clicked',
    },

    // Experiment Events
    EXPERIMENT: {
        VIEW: 'experiment_viewed', // previous: 'experiment_view'
    },
};

